import React, { useState, useEffect } from 'react';
import {
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Container,
  Button,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Divider,
} from '@mui/material';
import { messaging } from '../firebase';
import { getToken, deleteToken } from 'firebase/messaging';
import { userService } from '../services/user.service';
import { NotificationSettings } from '../models';
import { updateUserProfile } from '../services/user.utils';
import { User } from 'firebase/auth';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

type FontSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

interface SettingsProps {
  user: User | null;
  onBibleVersionChange: (version: string) => void;
  onFontSizeChange: (size: string) => void;
}

const Settings: React.FC<SettingsProps> = ({
  user,
  onBibleVersionChange,
  onFontSizeChange,
}) => {
  const [isOptedIn, setIsOptedIn] = useState(false);
  const [notificationTime, setNotificationTime] = useState<Dayjs | null>(
    dayjs().hour(9).minute(0)
  );
  const [defaultBibleVersion, setDefaultBibleVersion] = useState('ESV');
  const [defaultFontSize, setDefaultFontSize] = useState('md');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState(
    'Preferences saved successfully!'
  );

  useEffect(() => {
    if (user) {
      const fetchUserSettings = async () => {
        try {
          const userData = await userService.getUser(user.uid);
          if (userData) {
            setIsOptedIn(userData.notifications?.optedIn || false);
            setNotificationTime(
              userData.notifications?.time
                ? dayjs(userData.notifications.time, 'HH:mm')
                : dayjs().hour(9).minute(0)
            );
            setDefaultBibleVersion(userData.defaultBibleVersion || 'ESV');
            setDefaultFontSize(userData.defaultFontSize || 'md');
          }
        } catch (error) {
          console.error('Error fetching user settings:', error);
        }
      };
      fetchUserSettings();
    }
  }, [user]);

  const handleOptInChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsOptedIn(event.target.checked);
  };

  const handleTimeChange = (newValue: Dayjs | null) => {
    if (newValue) {
      const minutes = newValue.minute();
      const roundedMinutes = Math.round(minutes / 30) * 30;
      const roundedTime = newValue.minute(roundedMinutes).second(0);

      if (minutes % 30 !== 0) {
        setSnackbarMessage('Time rounded to nearest 30 minute increment.');
        setOpenSnackbar(true);
      }

      setNotificationTime(roundedTime);
    }
  };

  const handleBibleVersionChange = (event: SelectChangeEvent<string>) => {
    const newVersion = event.target.value as string;
    setDefaultBibleVersion(newVersion);
    onBibleVersionChange(newVersion);
  };

  const handleFontSizeChange = (event: SelectChangeEvent<string>) => {
    const newSize = event.target.value as string;
    setDefaultFontSize(newSize);
    onFontSizeChange(newSize);
  };

  // Generate a unique device ID for this browser/device
  const getDeviceId = () => {
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = `device_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
  };

  const handleSave = async () => {
    if (user) {
      try {
        let notificationSettings: NotificationSettings | undefined = undefined;

        if (isOptedIn) {
          const currentToken = await getToken(messaging, {
            vapidKey:
              'BBFX2RPW2S5Yz6CSMRNko0DKppZAXt7akTGTEEj_ClInGZRVaJLKtnEbGhfRGmygRLrrOzJMId2qJBgWrIsLhbM',
          });
          if (currentToken) {
            const deviceId = getDeviceId();

            // Get existing tokens or migrate from old format
            const userData = await userService.getUser(user.uid);
            const existingTokens = userData?.notifications?.tokens || [];

            // Migrate old single token format if it exists
            const legacyToken = (userData?.notifications as any)?.token;
            if (
              legacyToken &&
              !existingTokens.some((t) => t.token === legacyToken)
            ) {
              existingTokens.push({
                token: legacyToken,
                deviceId: 'legacy_device',
                lastUsed: new Date(),
              });
            }

            // Update or add current device token
            const existingTokenIndex = existingTokens.findIndex(
              (t) => t.deviceId === deviceId
            );
            if (existingTokenIndex >= 0) {
              existingTokens[existingTokenIndex] = {
                token: currentToken,
                deviceId,
                lastUsed: new Date(),
              };
            } else {
              existingTokens.push({
                token: currentToken,
                deviceId,
                lastUsed: new Date(),
              });
            }

            notificationSettings = {
              tokens: existingTokens,
              optedIn: isOptedIn,
              time: notificationTime?.format('HH:mm'),
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            };
          }
        } else {
          await deleteToken(messaging);

          // Remove this device's token but keep others
          const deviceId = getDeviceId();
          const userData = await userService.getUser(user.uid);
          const existingTokens = userData?.notifications?.tokens || [];

          // Migrate old single token format if it exists
          const legacyToken = (userData?.notifications as any)?.token;
          if (
            legacyToken &&
            !existingTokens.some((t) => t.token === legacyToken)
          ) {
            existingTokens.push({
              token: legacyToken,
              deviceId: 'legacy_device',
              lastUsed: new Date(),
            });
          }

          const updatedTokens = existingTokens.filter(
            (t) => t.deviceId !== deviceId
          );

          notificationSettings = {
            tokens: updatedTokens.length > 0 ? updatedTokens : undefined,
            optedIn: false,
            time: null,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          };
        }

        // Update profile settings
        await updateUserProfile(user.uid, {
          defaultBibleVersion,
          defaultFontSize,
          notifications: notificationSettings,
        });

        setSnackbarMessage('Preferences saved successfully!');
        setOpenSnackbar(true);
      } catch (error) {
        console.error('Error updating user settings:', error);
        setSnackbarMessage('Error saving preferences. Please try again.');
        setOpenSnackbar(true);
      }
    } else {
      console.error('User is not authenticated');
    }
  };

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  const getTextSizeClasses = (size: FontSize) => {
    const sizeMap: Record<FontSize, Record<string, string>> = {
      xs: {
        heading: 'text-base',
        content: 'text-xs',
      },
      sm: {
        heading: 'text-lg',
        content: 'text-sm',
      },
      md: {
        heading: 'text-xl',
        content: 'text-base',
      },
      lg: {
        heading: 'text-2xl',
        content: 'text-lg',
      },
      xl: {
        heading: 'text-3xl',
        content: 'text-xl',
      },
      '2xl': {
        heading: 'text-4xl',
        content: 'text-2xl',
      },
    };
    return sizeMap[size] || sizeMap.md;
  };

  return (
    <Container maxWidth='sm' sx={{ mb: 1 }}>
      <Box
        display='flex'
        flexDirection='column'
        alignItems='center'
        mt={4}
        p={4}
        boxShadow={3}
        borderRadius={3}
        bgcolor='background.paper'
        sx={{ maxWidth: 600 }}
      >
        <Typography variant='h4' component='h1' gutterBottom>
          Settings
        </Typography>

        <Box mt={3} width='100%'>
          <Typography
            variant='h6'
            component='h2'
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            Daily Reading Reminders
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            Get notified daily to maintain your Bible reading habit
          </Typography>
          <FormControlLabel
            control={
              <Switch checked={isOptedIn} onChange={handleOptInChange} />
            }
            label='Remind me to read'
          />
        </Box>
        <Box mt={2} width='100%'>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Choose the time you would like to receive your notification each day
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <TimePicker
              label='Notification Time'
              value={notificationTime}
              onChange={handleTimeChange}
              disabled={!isOptedIn}
              sx={{ width: '100%' }}
              timeSteps={{ minutes: 30 }}
            />
          </LocalizationProvider>
        </Box>
        <Divider sx={{ my: 4, width: '100%' }} />
        <Box mt={4} width='100%'>
          <Typography
            variant='h6'
            component='h2'
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            Bible Version
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            Choose your preferred Bible translation for reading verses
          </Typography>
          <FormControl fullWidth>
            <InputLabel id='bible-version-label'>
              Default Bible Version
            </InputLabel>
            <Select
              labelId='bible-version-label'
              value={defaultBibleVersion}
              onChange={(event) => handleBibleVersionChange(event)}
              label='Default Bible Version'
            >
              <MenuItem value='ESV'>ESV</MenuItem>
              <MenuItem value='WEB'>WEB</MenuItem>
              <MenuItem value='KJV'>KJV</MenuItem>
              <MenuItem value='ASV'>ASV</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Divider sx={{ my: 4, width: '100%' }} />
        <Box mt={4} width='100%'>
          <Typography
            variant='h6'
            component='h2'
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            Display Preferences
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            Customize the appearance of Bible verses for comfortable reading
          </Typography>
          <FormControl fullWidth>
            <InputLabel id='font-size-label'>Default Font Size</InputLabel>
            <Select
              labelId='font-size-label'
              value={defaultFontSize}
              onChange={(event) => handleFontSizeChange(event)}
              label='Default Font Size'
            >
              <MenuItem value='xs'>Extra Small</MenuItem>
              <MenuItem value='sm'>Small</MenuItem>
              <MenuItem value='md'>Medium</MenuItem>
              <MenuItem value='lg'>Large</MenuItem>
              <MenuItem value='xl'>Extra Large</MenuItem>
              <MenuItem value='2xl'>Double Extra Large</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box mt={3} width='100%'>
          <Typography
            variant='subtitle2'
            color='text.secondary'
            gutterBottom
            sx={{ fontWeight: 500 }}
          >
            Font Size Preview
          </Typography>
          <Box
            p={3}
            border={1}
            borderColor='grey.300'
            borderRadius={2}
            bgcolor='grey.50'
            sx={{ boxShadow: 1 }}
          >
            <div
              className={`font-bold text-gray-800 mb-2 ${
                getTextSizeClasses(defaultFontSize as FontSize).heading
              }`}
            >
              John 3:16
            </div>
            <div
              className={`${
                getTextSizeClasses(defaultFontSize as FontSize).content
              } text-gray-700`}
            >
              For God so loved the world, that he gave his only Son, that
              whoever believes in him should not perish but have eternal life.
            </div>
          </Box>
        </Box>
        <Divider sx={{ my: 4, width: '100%' }} />
        <Box mt={4} display='flex' justifyContent='center'>
          <Button variant='contained' color='primary' onClick={handleSave}>
            Save Preferences
          </Button>
        </Box>
      </Box>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity='success'
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Settings;
